import { ExternalLink, Sparkles } from "lucide-react";
import { FadeIn, Container, SectionLabel } from "@/components/Section";
import {
  TOK_PARTNERS,
  PARTNER_TIERS,
  PARTNER_CATEGORIES,
  getPartnersForProgramme,
  getTrustBandPartners,
} from "@/lib/partners";

/**
 * Cinematic, typography-led partner showcase.
 *
 * - Default behaviour: types each partner's name in TOK Display font with
 *   a short relationship line and a category tag. This keeps page weight
 *   low and the visual atmosphere unified — no mismatched JPG logos.
 *
 * - When a partner has a `logoUrl`, we render a refined grayscale image
 *   instead (filter: grayscale + brightness boost on hover) so the wall
 *   still feels coherent. Logos are width-capped to keep page-speed clean.
 *
 * Props:
 *   eyebrow, title, copy, partners (optional), variant ("hero" | "strip" | "card")
 *   programmeKey (optional) — filter partners by programme tag
 *   tier (optional) — filter by "flagship" / "supporter" / "ecosystem"
 *   compact — minimal strip mode for footer / hero proof bands
 */
export default function PartnersStrip({
  eyebrow = "In partnership with",
  title,
  copy,
  partners,
  programmeKey,
  tier,
  variant = "card",
  compact = false,
  withContainer = true,
  className = "",
  testId = "partners-strip",
}) {
  let list = partners;
  if (!list) {
    list = TOK_PARTNERS;
    if (programmeKey) list = getPartnersForProgramme(programmeKey);
    if (tier) list = list.filter((p) => p.tier === tier);
  }

  if (!list || list.length === 0) return null;

  const inner = (
    <div data-testid={testId} className={className}>
      {(eyebrow || title || copy) && !compact && (
        <FadeIn className="mb-10">
          {eyebrow && <SectionLabel>{eyebrow}</SectionLabel>}
          {title && (
            <h2 className="tok-headline-xl mt-5">
              {title.split(" ").slice(0, -1).join(" ")}{" "}
              <span className="text-amber-400">
                {title.split(" ").slice(-1)}
              </span>
            </h2>
          )}
          {copy && (
            <p className="mt-6 text-zinc-300 leading-relaxed max-w-3xl">
              {copy}
            </p>
          )}
        </FadeIn>
      )}

      {compact ? (
        <CompactBand list={list} />
      ) : variant === "strip" ? (
        <RefinedStrip list={list} />
      ) : (
        <PartnerGrid list={list} />
      )}
    </div>
  );

  return withContainer ? <Container>{inner}</Container> : inner;
}

// ---------- Variants ----------
function PartnerGrid({ list }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {list.map((p) => (
        <PartnerCard key={p.id} partner={p} />
      ))}
    </div>
  );
}

function RefinedStrip({ list }) {
  return (
    <div className="flex flex-wrap gap-x-10 gap-y-6 items-baseline">
      {list.map((p) => (
        <PartnerName key={p.id} partner={p} className="text-2xl sm:text-3xl" />
      ))}
    </div>
  );
}

function CompactBand({ list }) {
  return (
    <div
      data-testid="partners-compact-band"
      className="flex flex-wrap gap-x-6 gap-y-3 items-baseline"
    >
      {list.map((p) => (
        <span
          key={p.id}
          data-testid={`partner-name-${p.id}`}
          className="font-display font-bold uppercase tracking-tight text-zinc-300 text-base sm:text-lg"
        >
          {p.name}
        </span>
      ))}
    </div>
  );
}

// ---------- Cells ----------
function PartnerCard({ partner }) {
  const cat = PARTNER_CATEGORIES[partner.category]?.label || partner.category;
  return (
    <a
      href={partner.websiteUrl || "#"}
      target={partner.websiteUrl ? "_blank" : undefined}
      rel={partner.websiteUrl ? "noopener noreferrer" : undefined}
      data-testid={`partner-card-${partner.id}`}
      className={`group block p-6 sm:p-7 border border-white/5 bg-zinc-950 transition-colors ${
        partner.websiteUrl ? "hover:border-amber-400/40" : "cursor-default"
      } h-full`}
    >
      <div className="flex items-center gap-3 mb-5">
        <span className="inline-block w-2 h-2 bg-amber-400" />
        <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
          {PARTNER_TIERS[partner.tier]}
        </p>
      </div>

      {partner.logoUrl ? (
        <div className="h-12 mb-4">
          <img
            src={partner.logoUrl}
            alt={partner.name}
            loading="lazy"
            className="h-full w-auto object-contain max-w-full opacity-80 group-hover:opacity-100 grayscale group-hover:grayscale-0 transition-all"
          />
        </div>
      ) : (
        <PartnerName partner={partner} className="text-2xl sm:text-3xl mb-3" />
      )}

      <p className="text-sm text-zinc-300 leading-relaxed">{partner.role}</p>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          {cat}
        </span>
        {partner.websiteUrl && (
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-bold text-amber-400 group-hover:gap-2 transition-all">
            Visit <ExternalLink className="h-3 w-3" />
          </span>
        )}
      </div>
    </a>
  );
}

function PartnerName({ partner, className = "" }) {
  return (
    <p
      data-testid={`partner-name-${partner.id}`}
      className={`font-display font-black uppercase leading-[0.95] tracking-tight text-white ${className}`}
    >
      {partner.name}
    </p>
  );
}

// ---------- Pre-baked sections ----------

/** Slim trust band — designed to sit near the hero on Home / Impact. */
export function PartnersTrustBand({ className = "" }) {
  const list = getTrustBandPartners();
  if (!list.length) return null;
  return (
    <section
      data-testid="partners-trust-band"
      className={`relative border-y border-amber-400/10 bg-black py-12 ${className}`}
    >
      <Container>
        <div className="grid lg:grid-cols-[auto,1fr] gap-x-12 gap-y-6 items-center">
          <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" />
            Funded · supported · in partnership with
          </p>
          <div className="flex flex-wrap gap-x-8 gap-y-3 items-baseline">
            {list.map((p) => (
              <span
                key={p.id}
                data-testid={`partners-trust-band-${p.id}`}
                className="font-display font-bold uppercase tracking-tight text-zinc-200 text-lg sm:text-xl whitespace-nowrap"
              >
                {p.name}
              </span>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}
